import { base64ToArray, arrayBufferToBase64, handleCeremonyError, isVisible } from "./utils";
import { browserSupportsPasskeyAutofill } from "./browser_supports_passkey_autofill";
import { AbortControllerService } from "./abort_controller";

export const AuthenticationHook = {
    mounted() {
        console.info(`AuthenticationHook mounted`);

        this.checkConditionalUIAvailable(this).catch((error) => {
            console.info("Could not start webauthn autofill", error);
        });

        const authButton = document.getElementById("authentication-component");

        authButton?.addEventListener("click", async (event) => {
            this.pushEventTo(this.el, "generate_auth_challenge", {}, (reply, ref) => {
                this.verify_auth_challenge(reply, "optional", this);
            });
        });
    },

    async verify_auth_challenge(challenge, mediation, context) {
        try {
            const challengeArray = base64ToArray(challenge.challenge);

            let publicKey;
            if (mediation === "optional") {
                publicKey = {
                    allowCredentials: challenge.allowCredentials,
                    challenge: challengeArray.buffer,
                    rpId: challenge.rpId,
                    timeout: 60000,
                    userVerification: challenge.userVerification,
                };
            } else if (mediation === "conditional") {
                publicKey = {
                    allowCredentials: challenge.allowCredentials,
                    challenge: challengeArray.buffer,
                    rpId: challenge.rpId,
                    userVerification: challenge.userVerification,
                };
            }

            const credential = await AbortControllerService.run((signal) =>
                navigator.credentials.get({
                    publicKey,
                    signal,
                    mediation: mediation,
                    password: false,
                }),
            );

            const { rawId, response, type } = credential;
            const { clientDataJSON, authenticatorData, signature, userHandle } = response;
            const rawId64 = arrayBufferToBase64(rawId);
            const clientDataArray = Array.from(new Uint8Array(clientDataJSON));
            const authenticatorData64 = arrayBufferToBase64(authenticatorData);
            const signature64 = arrayBufferToBase64(signature);
            const userHandle64 = arrayBufferToBase64(userHandle);

            const data = {
                rawId64,
                type,
                clientDataArray,
                authenticatorData64,
                signature64,
                userHandle64,
            };

            // Awaited so a disconnected socket is reported as the assertion we
            // failed to hand back, rather than as a bare unhandled rejection.
            await this.pushEventTo(this.el, "authentication-attestation", data);
        } catch (error) {
            handleCeremonyError(error, context, { automatic: mediation === "conditional" });
        }
    },

    async checkConditionalUIAvailable(context) {
        // A browser without autofill support, or a page without a webauthn input,
        // simply has no conditional ceremony to start. Returning rather than
        // throwing keeps this fire-and-forget call from becoming an unhandled
        // rejection that error monitoring reports on every page load.
        if (!(await browserSupportsPasskeyAutofill())) {
            console.info("Browser does not support webauthn autofill");
            return;
        }

        // Check for an <input> with "webauthn" in its `autocomplete` attribute.
        // A hidden one still matches the selector but can never surface the
        // autofill prompt, so its ceremony would hold the browser's single
        // credentials slot open until the next button tap collided with it.
        const eligibleInputs = Array.from(document.querySelectorAll("input[autocomplete$='webauthn']")).filter(
            isVisible,
        );

        // WebAuthn autofill requires at least one valid input
        if (eligibleInputs.length < 1) {
            console.info(
                'No visible <input> with "webauthn" as the only or last value in its `autocomplete` attribute was detected',
            );
            return;
        }

        this.pushEventTo(context.el, "generate_auth_challenge", {}, async (challenge, ref) => {
            this.verify_auth_challenge(challenge, "conditional", context);
        });
    },
};
